/**
 * The heads-up display, and street-name labels floating over the world.
 *
 * Both exist for one reason: **so you can tell where you are.** A voxel
 * landscape with no signposts is disorienting even when it is your own home
 * town, and the fastest way to check the terrain is right is to stand on a
 * street you know and read its name and height.
 */

import type { Camera, Vector3 } from 'three';
import { Vector3 as V3 } from 'three';
import { t, type Stack, type WorldTime } from '@lofi/shared';
import { HudCluster } from './hud-cluster.ts';
import type { SlotContent } from './hud-parts.ts';
import type { AimTone } from './hud-scene.ts';
import { nameIsLegible } from './nameplate.ts';

export interface NearbyRoad {
  readonly name: string;
  readonly distance: number;
  readonly x: number;
  readonly z: number;
  /** Ground height at that point, in metres. Labels sit just above it. */
  readonly y: number;
}

export interface HudReadout {
  /**
   * What day and time it is in the world, or `null` before the server says (T103).
   *
   * 🔴 **This is in the HUD from the first commit of the clock, deliberately.**
   * A clock bug is a sunrise at the wrong hour or a day that turns over twice —
   * nothing anybody finds by reading code, and everything you find by glancing
   * at a corner of the screen while doing something else. Every later piece of
   * the weather work is checked against this row.
   */
  readonly worldTime: WorldTime | null;
  /**
   * Which zone and which archived real year, for diagnosis (T103 piece ④).
   *
   * Among the dim rows: the player is never shown a year (D41), but when the
   * weather looks wrong this is the line that says which of thirty years and
   * which of five zones to go and check.
   */
  readonly weatherSource: string | null;
  readonly street: string | null;
  readonly streetDistance: number | null;
  readonly standingOn: string | null;
  /**
   * 🏠 Which rung of D64's shelter ladder you are on (T132).
   *
   * 🔴 **Here so that it can be checked by WALKING**, which on this project has
   * caught more than the tests have — worldgen 7, 8 and 15 were all found by
   * haugur standing in the world looking at it. Step into a hut and this must
   * say *indoors*; step out and it must say *open ground*.
   *
   * ⚠️ **Not dim, for now.** It moves to the dim rows the day the exposure gauge
   * shows the same thing (T131); until then this row is the only way to see the
   * answer at all.
   */
  readonly shelter: string | null;
  /**
   * 🌊 **Whether the game thinks you are under water, and how deep** (T61).
   *
   * 🔴 **Built because the feature shipped green and did nothing, and nothing on
   * screen could say WHICH nothing.** A photograph of the seabed cannot tell
   * *the sea is switched off* from *the sea is on and the numbers are wrong*
   * from *the sea is on and something draws over it* — **and those have three
   * different fixes.**
   *
   * ⚠️ **Not dim, for the same reason the shelter row is not**: while T61 is
   * unproven this is the only way to see the answer at all. **It moves down
   * among the diagnostics the day the sea is obviously working.**
   *
   * 👉 **It also says when the answer was CARRIED FORWARD** because the chunk
   * under your eye had not arrived — which looks identical to being dry and is
   * not.
   */
  readonly water: string | null;
  /**
   * 🌫️ **What the fog ACTUALLY is right now** — T61.
   *
   * 🔴 **Read off `scene.fog` itself, never off what the code meant to
   * set.** The sea was asked for correctly — the row above proves the game
   * knew it was 6.3 m down — **and the picture did not change**, which leaves
   * two possibilities that look identical: the fog is set and something
   * downstream discards it, or something overwrites it before the frame draws.
   *
   * 👉 **This row tells them apart in one glance**, which nothing else
   * could. ⚠️ **Dim**, because it is a diagnostic rather than a thing a
   * player needs — unlike the water row, which is unproven and therefore
   * loud.
   */
  readonly fog: string | null;
  readonly chunks: number;
  /** Render distance, in chunks. */
  readonly viewRadius: number;
  /** Chunks queued or in flight — non-zero means the world is still arriving. */
  readonly pending: number;
  readonly fps: number;
  /**
   * 🔴 **The longest single frame in the last second, in milliseconds** — T186.
   *
   * 🎯 **haugur, 2026-09-15:** *"i get a little tearing and studdering… Still
   * my hud s telling me that im at 60fps."* **He was right and this row was
   * wrong**: `fps` is a smoothed average, and a stutter is the rare bad frame
   * an average is built to ignore. **This is the number that sees it.**
   */
  readonly worstFrameMs: number;
  /** How many frames in the last second took longer than `HITCH_MS`. */
  readonly hitches: number;
  /**
   * ⏱️ **The median wait from asking for a chunk to it arriving**, in
   * milliseconds — T186.
   *
   * 🔴 **Not a ping.** What a player feels when they fly is *ask for ground →
   * stand on ground*, which includes the server generating it — and on
   * 2026-09-15 that was where nearly all of the time went.
   *
   * ⚠️ **`null` until a chunk has actually arrived, and it must stay `null`.**
   * Nothing measured yet is not the same as *no wait*, and **zero milliseconds
   * reads as instant** — which is the exact opposite of the truth in the one
   * moment it would be shown: the first seconds after arriving, when the world
   * is still coming and nothing has landed.
   */
  readonly chunkWaitMs: number | null;
  readonly flying: boolean;
  /** Camera pull-back distance in metres. 0 is first person. */
  readonly zoom: number;
  /** What the camera got after any ground in the way stopped it. */
  readonly effectiveZoom: number;
  /** Where the view is pointing vertically, in radians. */
  readonly pitch: number;
  /** The player's mouse-look multiplier. */
  readonly lookScale: number;
  /** Flight speed multiplier — builds up while a direction is held. */
  readonly flightBoost: number;
  readonly locked: boolean;
  /**
   * 🎒 Whether the pack is open (T152).
   *
   * 🔴 **Here rather than inside `PackPanel`, because it changes what the
   * CONTROLS row says**, and that row is the HUD's. The panel itself carries
   * only "E or Esc to close" — which is the picture haugur approved — so the
   * line explaining how to move something has to live in the one place the
   * design already reserved for telling a player what the keys do
   * (`Design/00_Visual_Language.md` §13.10).
   */
  readonly packOpen: boolean;
  /**
   * Whether the server is sending the flat debug view (T49).
   *
   * ⚠️ Must be the server's answer, not the client's request — see `main.ts`.
   */
  readonly flat: boolean;
  /**
   * Whether the player is outside `TEST_AREA` — where no terrain is generated.
   *
   * ⚠️ Only possible since the Dev Tools window gained a teleport (T73). It is
   * shown as a banner because the symptom otherwise is *nothing at all*: no
   * ground, no error, no chunks arriving, which looks exactly like a server
   * that has stopped answering.
   */
  readonly outsideWorld: boolean;
  /** Icelandic name of the block that would be placed by a right click (T52). */
  readonly holding: string | null;
  /**
   * What is under the crosshair, as a finished line, or null for sky (T52).
   * Drawn in the MIDDLE above the bars since 2026-10-02 — see `AIM_Y`.
   */
  readonly target: string | null;
  /** 🐑 T286 — how urgent {@link target} is; colours the aim line. */
  readonly targetTone: AimTone;
  /**
   * The server's reason for refusing the last edit, or null (T52).
   *
   * ⚠️ Shown, not just logged. "I clicked and nothing happened" is the least
   * diagnosable bug report there is, and the person filing it cannot read the
   * server's logs (D2).
   */
  readonly notice: string | null;
  /**
   * 🙌 **The animal in your arms, already in words, or `null`** — T261.
   *
   * 🔴 **A ROW RATHER THAN A PASSING NOTICE.** Carrying is a STATE you are in
   * until you do something about it, and a message that fades after three
   * seconds cannot tell you *"you are still holding a sheep"* two minutes
   * later. ⚠️ **Walking around with a sheep and no way to see that you are** is
   * the sort of thing that reads as the sheep having vanished.
   */
  readonly carrying: string | null;
}

export class Hud {
  private readonly root: HTMLDivElement;
  private readonly labelLayer: HTMLDivElement;
  private readonly labels = new Map<string, HTMLDivElement>();
  /**
   * 🧍 **One name plate per connection** — T176, keyed by connection id.
   *
   * ⚠️ **Not keyed by the name**, which {@link labels} can get away with
   * because a street is its name. Two windows on one account share a name and
   * are two different people to look at.
   */
  private readonly nameplates = new Map<string, HTMLDivElement>();
  private readonly values = new Map<string, HTMLElement>();
  /**
   * The survival HUD — health, food, exposure and the ten on-hand slots
   * (T149). ⚠️ It also owns the control list now; see `showControls`.
   */
  private readonly cluster: HudCluster;
  /** What the control list currently says, so the fade restarts only on a change. */
  private controlsText: string | null = null;
  /** Pending fade of the control list, so a rapid change cannot leave two. */
  private controlsTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly notice: HTMLDivElement;
  private readonly flatBanner: HTMLDivElement;
  private readonly outsideBanner: HTMLDivElement;
  private readonly projected = new V3();
  private lastRender = 0;

  constructor() {
    this.labelLayer = document.createElement('div');
    this.labelLayer.className = 'labels';
    document.body.appendChild(this.labelLayer);

    /**
     * ⚠️ The flat-view banner (T49).
     *
     * Across the top of the screen, in the one colour nothing else in this
     * world uses, and impossible to mistake for scenery. The failure this
     * prevents is specific and expensive: someone looks at a debug view,
     * believes it is the terrain, and spends a session explaining why Iceland
     * has gone flat.
     */
    /* ⚠️ Both banners live in one stack. They can be on at the same time —
       flatten the world, then teleport out of it — and two independently
       `position: fixed; top: 0` elements would sit exactly on top of each
       other, hiding whichever was painted first. */
    const banners = document.createElement('div');
    banners.className = 'banners';
    document.body.appendChild(banners);

    this.flatBanner = document.createElement('div');
    this.flatBanner.className = 'flat-banner';
    this.flatBanner.textContent = t('hud.banner.flat');
    this.flatBanner.style.display = 'none';
    banners.appendChild(this.flatBanner);

    /**
     * ⚠️ The outside-the-world banner (T73).
     *
     * The world is a 1,400 × 1,150 m rectangle of Hafnarfjörður and the Dev
     * Tools search covers all 103,000 km² of Iceland, so teleporting to
     * Akureyri is one click away and lands you where **no chunk will ever
     * arrive**. Without this the screen is simply empty, which is
     * indistinguishable from a broken server, a failed deploy or a hung
     * generator — all of which this project has spent time on before.
     */
    this.outsideBanner = document.createElement('div');
    this.outsideBanner.className = 'outside-banner';
    this.outsideBanner.textContent = t('hud.banner.outside');
    this.outsideBanner.style.display = 'none';
    banners.appendChild(this.outsideBanner);

    this.root = document.createElement('div');
    this.root.className = 'hud';

    /**
     * Built once. `render` only ever updates text.
     *
     * ⚠️ **Five rows left this panel on 2026-08-11** — the date, the weather, the
     * elevation, the ISN93 coordinate and who you are. They are the top bar's
     * now (`topbar.ts`), at haugur's request, and the card shrank by exactly
     * that much.
     *
     * 🔴 **What stayed is not an accident.** The bar answers *what is true of the
     * world right now* — the time, the sky, where you are. This card answers
     * **what is true of the thing in front of you**: the street, what you are
     * standing on, what you are aiming at, what you would place. Those change as
     * you turn your head; the bar's readings do not.
     */
    for (const [key, label, dim] of [
      ['street', t('hud.row.street'), false],
      ['standing', t('hud.row.standing'), false],
      /* 🏠 Beside 'standing on', because both answer *where am I* (T132). */
      ['shelter', t('hud.row.shelter'), false],
      /* 🌊 Beside the shelter row, because both answer *where am I* — and
         this one exists because the sea shipped green and invisible (T61). */
      ['water', t('hud.row.water'), false],
      /* Building (T52). Above the dim rows, because it changes with every
         click and the diagnostics below it do not.
         🎯 **"Looking at" LEFT this card on 2026-10-02** — it is the aim line in
         the middle of the screen now, above the bars (`hud-scene.ts` `AIM_Y`):
         *"the whole Looking at text should always appear in middle of sceen
         above health bars for players to notice."* */
      ['holding', t('hud.row.holding'), false],
      /**
       * 🙌 T261. ⚠️ Beside "Holding", which is about your HANDS — this is about
       * your ARMS, and the two are genuinely different things.
       *
       * 🔴 **IT READS "In your arms", NOT "Carrying", AND THAT IS DELIBERATE.**
       * The guide chapter *Breaking, Carrying and the Pack* has meant *"blocks
       * in your pack"* by **carrying** since the beginning. ⚠️ **A second thing
       * sharing one word is this repo's oldest shape of fault** — a skemma
       * called *Búr*, an `isFoliage` that had to mean *a roof too* — so the
       * player-facing words are kept apart even though the wire field is still
       * `carrying`.
       */
      ['carrying', t('hud.row.carrying'), false],
      /* The absolute day counter, down among the diagnostics: the player is
         never shown a year (D41), but the number that never resets is what
         anything with a duration will be measured against, so it belongs
         somewhere it can be quoted in a report. */
      /* 🌫️ Dim: a diagnostic for T61, not a player reading. */
      ['fog', t('hud.row.fog'), true],
      ['day', t('hud.row.day'), true],
      ['zone', t('hud.row.zone'), true],
      ['chunks', t('hud.row.chunks'), true],
      ['fps', t('hud.row.fps'), true],
      ['view', t('hud.row.view'), true],
      /* Pitch and sensitivity are here so a report of "the view flipped" can
         come with the number it flipped at, instead of a description. */
      ['look', t('hud.row.look'), true],
    ] as const) {
      const { row, value } = makeRow(label, dim);
      this.root.appendChild(row);
      this.values.set(key, value);
    }

    /**
     * The survival HUD, and with it the controls line.
     *
     * 🔴 **The controls line moved INTO the cluster on 2026-08-18, and that
     * is the collision fix.** It was its own strip at `bottom: 12px` — which is
     * exactly where the ten on-hand slots go. The design had already reserved a
     * row for it inside the cluster (`Design/00` §13.10), so nothing here had
     * to be invented.
     *
     * ⚠️ **It used to live inside the panel before that, and it decided the
     * panel's width.** A `position: fixed` box with no width shrink-to-fits its
     * widest line, and the locked control list is fourteen items on one line —
     * so the moment haugur clicked to play, a 232 px card became a ~900 px slab
     * carrying eleven rows of numbers he had not asked to make bigger. The card
     * has a `max-width` now and cannot be stretched by anything.
     *
     * It still **fades out once you have read it.** Controls are learned once;
     * a permanent list is clutter for everyone who already knows them. It
     * returns whenever it has something different to say — walking to flying,
     * or releasing the mouse — because that is exactly when it is worth
     * reading again.
     */
    this.cluster = new HudCluster();

    /**
     * Why the last edit was refused (T52).
     *
     * ⚠️ Its own line rather than a row, because it is a *sentence* and the
     * rows are label-and-value pairs. Hidden when there is nothing to say, so
     * the panel does not carry a permanently empty line.
     */
    this.notice = document.createElement('div');
    this.notice.className = 'notice';
    this.notice.style.display = 'none';
    this.root.appendChild(this.notice);

    document.body.appendChild(this.root);
  }

  /**
   * Built from DOM nodes rather than `innerHTML`.
   *
   * Street names come out of the geodata database, and `nafnfitju` is text
   * somebody else authored. `textContent` cannot execute anything, whatever is
   * in it — which is a stronger guarantee than remembering to escape.
   *
   * ⚠️ **Rows are created once and their text updated in place.** The first
   * version rebuilt all seven elements every frame, which forces a layout 60
   * times a second for information a human cannot read that fast. Throttled to
   * ~6 Hz as well: nothing here changes meaningfully faster.
   */
  render(r: HudReadout): void {
    const now = performance.now();
    if (now - this.lastRender < 160) return;
    this.lastRender = now;

    const unknown = t('common.unknown');

    const street = r.street
      ? r.streetDistance !== null && r.streetDistance > 2
        ? t('hud.street.withDistance', { street: r.street, distance: r.streetDistance.toFixed(0) })
        : r.street
      : unknown;

    this.flatBanner.style.display = r.flat ? 'block' : 'none';
    this.outsideBanner.style.display = r.outsideWorld ? 'block' : 'none';

    this.set('day', r.worldTime ? String(r.worldTime.absoluteDay) : unknown);
    this.set('zone', r.weatherSource ?? unknown);

    this.set('street', street);
    /* 🐑 The aim line, in the middle above the bars. ⚠️ Hidden while the pack is
       open — the panel's bottom edge sits on the health rod, so the line would
       be drawn under it. Looking at the sky draws nothing at all. */
    this.cluster.setAim(r.packOpen ? null : r.target, r.targetTone);
    this.set('holding', r.holding ?? unknown);
    /* 🙌 T261. ⚠️ "Nothing" rather than an em dash: empty arms is a real
       answer, not a missing one. */
    this.set('carrying', r.carrying ?? t('hud.carrying.nothing'));
    this.set('shelter', r.shelter ?? unknown);
    this.set('water', r.water ?? unknown);
    this.set('fog', r.fog ?? unknown);

    this.notice.textContent = r.notice ?? '';
    this.notice.style.display = r.notice === null ? 'none' : 'block';
    /* While flying this row otherwise reads "Loft" — air — which tells you
       nothing. The speed you are actually doing is worth more. */
    this.set(
      'standing',
      r.flying
        ? t('hud.standing.flying', { boost: r.flightBoost.toFixed(1) })
        : (r.standingOn ?? unknown),
    );
    /* Loaded, how far the ring reaches in metres, and what is still coming.
       The last one is the difference between "the world ends here" and "the
       world has not arrived yet", which look identical otherwise. */
    const distance = r.viewRadius * 32;
    this.set(
      'chunks',
      r.pending > 0
        ? /* ⏱️ How long each one is taking. 🔴 The difference between "my
             connection is slow" and "the server is thinking", which the word
             *laggy* does not say and nobody could read off the old row.
             ⚠️ **A whole different sentence until one has actually arrived**,
             rather than a zero — see `chunkWaitMs`. */
          r.chunkWaitMs === null
          ? t('hud.chunks.loadingUnmeasured', {
              chunks: String(r.chunks),
              distance: String(distance),
              pending: String(r.pending),
            })
          : t('hud.chunks.loading', {
              chunks: String(r.chunks),
              distance: String(distance),
              pending: String(r.pending),
              wait: r.chunkWaitMs.toFixed(0),
            })
        : t('hud.chunks.loaded', { chunks: String(r.chunks), distance: String(distance) }),
    );
    /* 🔴 The worst frame is shown BESIDE the average, not instead of it, and
       only when there is one worth showing (T186). Both numbers are true; they
       answer different questions. The average says *is this machine coping*,
       and the worst says *why did that feel horrible*. ⚠️ Showing the worst
       always would make an idle 51 ms frame look like a fault. */
    this.set(
      'fps',
      r.hitches > 0
        ? t('hud.fps.hitching', {
            fps: r.fps.toFixed(0),
            worst: r.worstFrameMs.toFixed(0),
            hitches: String(r.hitches),
          })
        : t('hud.fps.steady', { fps: r.fps.toFixed(0) }),
    );
    /* Show both distances when the ground has pushed the camera in, so it is
       obvious that the camera stopped rather than the zoom being ignored. */
    const view =
      r.zoom > 0
        ? r.effectiveZoom < r.zoom - 0.5
          ? t('hud.view.backAndAsked', {
              effective: r.effectiveZoom.toFixed(0),
              asked: r.zoom.toFixed(0),
            })
          : t('hud.view.back', { distance: r.zoom.toFixed(0) })
        : t('hud.view.firstPerson');
    this.set('view', view);

    const degrees = (r.pitch * 180) / Math.PI;
    this.set(
      'look',
      t('hud.look.value', {
        degrees: `${degrees >= 0 ? '+' : ''}${degrees.toFixed(0)}`,
        scale: r.lookScale.toFixed(2),
      }),
    );

    /* ⚠️ Each control line is ONE key, not a list of key-and-action fragments
       assembled here. A translator needs the whole line: the order, the
       separators and which verb goes with which key are all things another
       language may do differently (T124). */
    this.showControls(
      r.packOpen
        ? t('hud.controls.pack')
        : r.locked
          ? r.flying
            ? t('hud.controls.flying')
            : t('hud.controls.walking')
          : t('hud.controls.unlocked'),
      /* ⚠️ Never faded while the pack is open. The other three lines are
         learned once and then clutter; this one is the only thing on screen
         saying that a click picks a stack up, and it is read at the moment the
         panel opens — which is exactly when a nine-second timer would already
         have run out on the previous line. */
      false,
    );
  }

  /**
   * 🎒 What the player is carrying (protocol 9, D80).
   *
   * ⚠️ **Not part of `HudReadout` and not called per frame.** The readout is
   * rendered ~6 times a second off the render loop; an inventory changes when
   * the server says it changed, which is a few times a minute at most. Putting
   * it in the readout would redraw 34 slots of SVG for nothing.
   */
  /** 🙌 What is in the player's arms, or `null` — T261. */
  setCarrying(content: SlotContent | null): void {
    this.cluster.setCarrying(content);
  }

  setInventory(stacks: readonly Stack[], selected: number): void {
    this.cluster.setInventory(stacks, selected);
  }

  /**
   * 🍖 Health and food, and why they are moving (T129, protocol 12).
   *
   * ⚠️ **This one IS called per frame**, unlike the inventory above, and that is
   * deliberate rather than careless: the server corrects the bars every 30
   * seconds and the client carries them forward in between, so a bar that only
   * moved when a message arrived would visibly jump. The redraw is guarded
   * inside the cluster, which rounds to whole percent and does nothing when
   * nothing has changed.
   */
  setVitals(
    healthPercent: number,
    foodPercent: number,
    reason: string | null,
    /**
     * 🥶 The exposure gauge, **−1 (worst) … 0 (comfortable)** (T131, D84).
     *
     * ⚠️ **Snapped, never smooth** — it takes one of five fixed positions.
     * The warm half of the rod is drawn and unreachable: nothing in Iceland
     * makes you too hot.
     */
    exposure = 0,
  ): void {
    this.cluster.setVitals(healthPercent, foodPercent, reason, exposure);
  }

  /** How long the control list stays up before fading, in milliseconds. */
  private static readonly CONTROLS_VISIBLE_MS = 9000;

  /**
   * Put the control list up, and take it down again once it has been read.
   *
   * ⚠️ **Only fades while the mouse is captured.** Released, the line is six
   * words and is the only thing telling you how to start — hiding *that* would
   * leave a new player looking at a world with no way in.
   *
   * The timer restarts only when the text actually changes, so this does not
   * re-trigger sixty times a second.
   */
  private showControls(text: string, locked: boolean): void {
    if (this.controlsText !== text) {
      this.controlsText = text;
      this.cluster.setHint(text);
      this.cluster.fadeHint(false);
      if (this.controlsTimer !== null) clearTimeout(this.controlsTimer);
      this.controlsTimer = locked
        ? setTimeout(() => {
            this.cluster.fadeHint(true);
          }, Hud.CONTROLS_VISIBLE_MS)
        : null;
    }
  }

  private set(key: string, value: string): void {
    const el = this.values.get(key);
    /* Skip the write when nothing changed — assigning textContent dirties
       layout even when the string is identical. */
    if (el && el.textContent !== value) el.textContent = value;
  }

  /**
   * Place street names in the world.
   *
   * HTML elements projected to screen space rather than 3D text: text stays
   * crisp at any distance, needs no font atlas, and is readable — which is the
   * entire point of having it.
   */
  updateLabels(roads: readonly NearbyRoad[], camera: Camera, renderOrigin: Vector3): void {
    const seen = new Set<string>();

    for (const road of roads) {
      seen.add(road.name);

      let el = this.labels.get(road.name);
      if (!el) {
        el = document.createElement('div');
        el.className = 'label';
        el.textContent = road.name;
        this.labelLayer.appendChild(el);
        this.labels.set(road.name, el);
      }

      /* ⚠️ On the GROUND at that point, not at a fixed height.
         The first version pinned every label to y = 3 while the terrain around
         Hverfisgata is at 16 m — so labels hung 13 m below the street they
         named, and drifted against it as the camera moved, because they were
         at a different depth. Two metres above the road reads as a sign. */
      const shown = this.pinToScreen(el, road.x, road.y + 2, road.z, camera, renderOrigin);
      /* Fade with distance so far-off streets do not crowd the view. */
      if (shown) el.style.opacity = String(Math.max(0.25, 1 - road.distance / 400));
    }

    for (const [name, el] of this.labels) {
      if (!seen.has(name)) {
        el.remove();
        this.labels.delete(name);
      }
    }
  }

  /**
   * 🧍 **Write each person's name above their head** — T176, protocol 27.
   *
   * 🎯 **haugur, 2026-09-09:** *"i need to have players name above the head of
   * their carachter."*
   *
   * 🔴 **The same projection the street names use, deliberately.** ⚠️ **This
   * project has now written a second way in five times** — the hearth, the
   * driftwood, the pointer lock, breaking-vs-opening a house, and a camera fix
   * put into the wrong one of two functions that both place the camera. **A
   * name over a head and a name over a street are the same problem**, so they
   * go through {@link pinToScreen} and there is one place where "behind the
   * camera" is decided.
   *
   * ⚠️ **Nobody without a name reaches this** — {@link OtherPlayers.nameplates}
   * leaves them out, which on development is everybody.
   *
   * ⬜ **A name does NOT hide behind a hill.** The overlay is HTML over the
   * canvas and has no depth buffer to test against, so a name would otherwise
   * show through a mountain. 🎯 **Hidden past `NAMEPLATE_RANGE`, his 30 m** — **and that
   * is a limit, not an oversight.** A real depth test needs the name drawn in
   * the scene, which is the textured-material path this deliberately avoids.
   */
  updateNameplates(
    people: readonly { id: string; name: string; x: number; y: number; z: number }[],
    camera: Camera,
    renderOrigin: Vector3,
    /**
     * 🔴 **Where the PLAYER is, in world metres — not where the camera is.**
     *
     * 🔴 **THE BUG THIS PARAMETER EXISTS TO KILL, found by baking a picture.**
     * The first version measured from `camera.position`, and **`main.ts` rebases
     * the camera into render space on the line AFTER this call** — so it read
     * last frame's camera against this frame's origin, got a distance of some
     * hundreds of thousands of metres, and hid every name. ⚠️ **It hid them the
     * same way being out of range does**, so the screen looked like a working
     * limit with nobody near.
     *
     * ⚠️ **This is `06_Traps.md`'s "a second way in" arriving by a new door:**
     * not two functions that place the camera, but **reading the camera before
     * it has been placed.** ✅ Taking the player's own position removes the
     * ordering question rather than answering it.
     *
     * ✅ **And it is more correct on the merits.** 🎯 haugur's 30 m means 30 m
     * from *you*. The camera pulls back in third person, so measuring from it
     * would make names vanish because you zoomed out.
     */
    eye: { readonly x: number; readonly y: number; readonly z: number },
  ): void {
    const seen = new Set<string>();

    for (const person of people) {
      /* 🔴 Keyed by the connection id, NOT by the name. Two windows on one
         account are two bodies wearing one name, and keying by the name would
         give them a single plate that jumped between them. */
      seen.add(person.id);

      /* 🔴 HOW FAR is decided in `nameplate.ts`, where a test can reach it —
         this file needs a DOM and a camera. ✅ **Both points are in WORLD
         metres, so there is no rebasing here at all** — see {@link eye} for the
         version that did subtract, and hid every name on screen. */
      const close = nameIsLegible(eye, person);

      let el = this.nameplates.get(person.id);
      if (!el) {
        el = document.createElement('div');
        el.className = 'label nameplate';
        this.labelLayer.appendChild(el);
        this.nameplates.set(person.id, el);
      }
      /* ⚠️ Written only when it has actually changed — assigning `textContent`
         dirties layout even when the string is identical, and this runs every
         frame. The same rule `set()` above follows. */
      if (el.textContent !== person.name) el.textContent = person.name;

      if (!close) {
        el.style.display = 'none';
        continue;
      }
      this.pinToScreen(el, person.x, person.y, person.z, camera, renderOrigin);
    }

    for (const [id, el] of this.nameplates) {
      if (seen.has(id)) continue;
      /* 🔴 Somebody who has gone must lose their name in the same breath as
         their body — *"a frozen body and a departed body look identical"*, and
         a name left hanging in the air is worse than either. */
      el.remove();
      this.nameplates.delete(id);
    }
  }

  /**
   * 📍 **Put an HTML element where a world point appears on screen.**
   *
   * 🔴 **The one place this is worked out.** Street names and nameplates both
   * call it; a second copy is how the two would come to disagree about what
   * "behind you" means.
   *
   * @param x World metres — **not render metres**; `renderOrigin` is taken off
   *   here, which is the subtraction that once drew bodies 347 km outside the
   *   world when it was skipped.
   * @returns whether it ended up visible. ⚠️ **A caller that sets anything else
   *   on the element — opacity, say — must check this**, because a hidden
   *   element was not positioned and its old coordinates are still on it.
   */
  private pinToScreen(
    el: HTMLElement,
    x: number,
    y: number,
    z: number,
    camera: Camera,
    renderOrigin: Vector3,
  ): boolean {
    this.projected.set(x - renderOrigin.x, y - renderOrigin.y, z - renderOrigin.z);
    this.projected.project(camera);

    /* z > 1 means behind the camera; projecting it would mirror the label to
       the opposite side of the screen, which looks like a ghost street. */
    const behind = this.projected.z > 1;
    const offScreen = Math.abs(this.projected.x) > 1.1 || Math.abs(this.projected.y) > 1.1;

    if (behind || offScreen) {
      el.style.display = 'none';
      return false;
    }

    el.style.display = 'block';
    el.style.left = `${((this.projected.x + 1) / 2) * window.innerWidth}px`;
    el.style.top = `${((-this.projected.y + 1) / 2) * window.innerHeight}px`;
    return true;
  }
}

function makeRow(label: string, dim: boolean): { row: HTMLDivElement; value: HTMLElement } {
  const row = document.createElement('div');
  row.className = dim ? 'row dim' : 'row';

  const key = document.createElement('span');
  key.textContent = label;

  const value = document.createElement('b');

  row.append(key, value);
  return { row, value };
}
